<template>
  <div class="ml-56">
    <!-- Page content -->
    <div class="w-full h-full min-h-screen overflow-y-scroll bg-truegray-100">
      <!-- Page header -->
      <PageHeader :overline="headerConfig.overline" :title="headerConfig.title">
        <template #title>
          <div class="flex items-center space-x-4 w-3/4">
            <div class="px-8 lg:border-l-8 border-truegray-100">
              <h2 class="text-cw-900 font-montserrat mb-10">
                {{ headerConfig.title }}
              </h2>
            </div>
          </div>
        </template>
      </PageHeader>
      <!-- /Page header -->

      <!-- Accounts content -->
      <div class="w-full py-16 px-8 md:px-16 lg:px-26 2xl:px-32">
        <div
          class="flex flex-row justify-between items-center gap-3 flex-wrap mb-8"
        >
          <div class="flex flex-col items-start justify-center">
            <FormulateInput
              key="showingConnectionsOnly"
              v-model="showingConnectionsOnly"
              name="showingConnectionsOnly"
              type="toggle"
              :label="
                showingConnectionsOnly
                  ? 'Viewing: Self-managed connections'
                  : 'Viewing: All accounts'
              "
            />
            <p class="text-xs text-truegray-500 mt-2">
              Toggle on to view accounts with only their own Connections access
              (self-managed connections). Toggle off to view all organisation
              accounts.
            </p>
          </div>
          <button
            class="btn btn-cw-cd-primary gap-2 mb-0"
            @click="() => openUpsertAccountModal()"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="w-5 h-5"
            >
              <path
                d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z"
              />
            </svg>
            <span>New Account</span>
          </button>
        </div>

        <!-- Loading -->
        <div
          v-if="isLoading"
          class="h-52 flex flex-col justify-center items-center"
        >
          <lottie :options="getLoadingAnimationsOptions('dark')" />
        </div>
        <!-- /Loading -->

        <!-- Accounts -->
        <template v-else>
          <div class="grid grid-cols-2 gap-4 w-full">
            <template v-if="accounts.length">
              <div
                v-for="account in accounts"
                :key="account.id"
                class="col-span-1 flex justify-between items-center flex-wrap gap-3 px-7 py-5 bg-white border border-truegray-300 hover:border-truegray-500 rounded-lg"
              >
                <!-- Details -->
                <div class="flex flex-col gap-1">
                  <div class="flex gap-2 items-center">
                    <span class="text-sm text-truegray-600">{{
                      account.name
                    }}</span>
                  </div>

                  <p class="text-sm text-truegray-600">{{ account.email }}</p>

                  <p class="text-truegray-400 text-sm">
                    {{ account.job_title }}
                  </p>

                  <!-- Role -->
                  <div class="flex flex-row items-center gap-2">
                    <svg
                      v-if="isAccountConnectionsOnly(account)"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      class="w-4 h-4 text-truegray-400"
                    >
                      <path
                        d="M7 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM14.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM1.615 16.428a1.224 1.224 0 0 1-.569-1.175 6.002 6.002 0 0 1 11.908 0c.058.467-.172.92-.57 1.174A9.953 9.953 0 0 1 7 18a9.953 9.953 0 0 1-5.385-1.572ZM14.5 16h-.106c.07-.297.088-.611.048-.933a7.47 7.47 0 0 0-1.588-3.755 4.502 4.502 0 0 1 5.874 2.636.818.818 0 0 1-.36.98A7.465 7.465 0 0 1 14.5 16Z"
                      />
                    </svg>

                    <svg
                      v-else
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      class="w-4 h-4 text-cw-alt"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.403 12.652a3 3 0 0 0 0-5.304 3 3 0 0 0-3.75-3.751 3 3 0 0 0-5.305 0 3 3 0 0 0-3.751 3.75 3 3 0 0 0 0 5.305 3 3 0 0 0 3.75 3.751 3 3 0 0 0 5.305 0 3 3 0 0 0 3.751-3.75Zm-2.546-4.46a.75.75 0 0 0-1.214-.883l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    <span class="text-truegray-400 text-sm">
                      {{ getRoleLabel(account) }}
                    </span>
                  </div>
                  <!-- /Role -->

                  <!-- Connections email receiver -->
                  <div
                    v-if="account.enable_connections_emails"
                    class="flex items-center gap-2 text-truegray-400 text-xs"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      class="w-3 h-3"
                    >
                      <path
                        d="M7 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM14.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM1.615 16.428a1.224 1.224 0 0 1-.569-1.175 6.002 6.002 0 0 1 11.908 0c.058.467-.172.92-.57 1.174A9.953 9.953 0 0 1 7 18a9.953 9.953 0 0 1-5.385-1.572ZM14.5 16h-.106c.07-.297.088-.611.048-.933a7.47 7.47 0 0 0-1.588-3.755 4.502 4.502 0 0 1 5.874 2.636.818.818 0 0 1-.36.98A7.465 7.465 0 0 1 14.5 16Z"
                      />
                    </svg>

                    Connections account
                  </div>
                  <!-- /Connections email receiver -->

                  <!-- Public contact details -->
                  <div
                    v-if="account.enable_public_contact"
                    class="flex items-center gap-2 text-truegray-400 text-xs"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      class="w-3 h-3"
                    >
                      <path
                        d="M3 4a2 2 0 0 0-2 2v1.161l8.441 4.221a1.25 1.25 0 0 0 1.118 0L19 7.162V6a2 2 0 0 0-2-2H3Z"
                      />
                      <path
                        d="m19 8.839-7.77 3.885a2.75 2.75 0 0 1-2.46 0L1 8.839V14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.839Z"
                      />
                    </svg>

                    Public contact details
                  </div>
                  <!-- /Public contact details -->
                </div>
                <!-- /Details -->

                <!-- Actions -->
                <div class="flex gap-5">
                  <!-- Edit -->
                  <div
                    title="Edit account"
                    class="flex items-center group text-sm text-truegray-300 hover:text-green-400 cursor-pointer transition-all duration-100"
                    @click="editAccount(account.id)"
                  >
                    <div class="flex items-center gap-4">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        class="w-6 h-6"
                      >
                        <path
                          d="m5.433 13.917 1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65Z"
                        />
                        <path
                          d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z"
                        />
                      </svg>
                    </div>
                  </div>
                  <!-- /Edit -->

                  <!-- Delete -->
                  <div
                    title="Remove account"
                    class="group flex items-center text-sm text-truegray-300 hover:text-red-600 cursor-pointer transition-all duration-100"
                    @click="deleteAccount(account.id)"
                  >
                    <div class="flex items-center gap-4">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        class="w-6 h-6"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                  </div>
                  <!-- /Delete -->
                </div>
                <!-- /Actions -->
              </div>
            </template>

            <div v-else>
              <p class="italic text-truegray-400">No accounts</p>
            </div>
          </div>
        </template>
        <!-- /Accounts -->
      </div>
      <!-- /Accounts content -->
    </div>
    <!-- /Page content -->
  </div>
</template>

<script>
import { defineComponent, computed, ref, onMounted, watch } from 'vue'
import { useStore } from '../../../js/store/index.js'
import PageHeader from '../components/PageHeader.vue'
import * as loadingAnimation from '../../../shared/components/loading.json'
import * as loadingAnimationDark from '../../../shared/components/loading-dark.json'
import { EventBus } from '../../../js/plugins/event-bus.js'

export default defineComponent({
  name: 'Accounts',

  components: {
    PageHeader,
  },

  setup() {
    const store = useStore()

    const isLoading = ref(false)
    const isSubmitting = ref(false)
    const getLoadingAnimationsOptions = (type) => {
      const animation =
        type === 'dark' ? loadingAnimationDark : loadingAnimation
      return {
        animationData: animation.default,
      }
    }
    const headerConfig = {
      overline: 'accounts',
      title: 'User Management',
    }
    const inputClasses =
      'flex justify-center items-center sm:text-sm text-truegray-700 focus:text-black active:text-black focus:outline-none active:outline-none transition-all duration-100 ease-in-out border border-truegray-300 focus:border-black active:border-black rounded bg-white px-4 mt-2'

    // Organisation
    const organisation = computed(
      () => store.getters['contributors/getOrganisation'],
    )
    const organisationId = computed(
      () => store.getters['contributors/getOrganisationId'],
    )

    // Accounts
    const accounts = computed(() => store.getters['contributors/getAccounts'])

    const showingConnectionsOnly = ref(false)
    watch(showingConnectionsOnly, (val) => {
      init(val)
    })

    const isAccountConnectionsOnly = (account) => {
      return account.connections_only
    }

    const getRoleLabel = (account) => {
      return isAccountConnectionsOnly(account)
        ? 'Self-managed connection'
        : 'Organisation Admin (Full Access)'
    }

    const editAccount = (id) => {
      openUpsertAccountModal(id)
    }

    const deleteAccount = async (id) => {
      openDeleteAccountModal(id)
    }

    const openDeleteAccountModal = (id = null) => {
      if (id) {
        EventBus.$emit('showModal', {
          action: 'cw-contributor-delete-account',
          data: {
            id,
          },
        })
      }
    }

    const openUpsertAccountModal = (id = null) => {
      EventBus.$emit('showModal', {
        action: 'cw-contributor-upsert-account',
        data: {
          id,
        },
      })
    }

    const onSubmit = async () => {}

    watch(organisationId, () => init())

    // Init
    const init = async (connectionsOnly = false) => {
      isLoading.value = true
      await store.dispatch('contributors/dispatchUpdateAccounts', {
        connectionsOnly,
      })
      isLoading.value = false
    }

    onMounted(() => {
      init()

      EventBus.$on('dispatchUpdateAccounts', async () => {
        init(showingConnectionsOnly.value)
      })
    })

    return {
      getLoadingAnimationsOptions,
      inputClasses,
      headerConfig,

      isLoading,
      isSubmitting,

      organisation,
      accounts,
      showingConnectionsOnly,

      isAccountConnectionsOnly,
      getRoleLabel,
      editAccount,
      deleteAccount,
      openUpsertAccountModal,
      onSubmit,
    }
  },
})
</script>
